import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Modal,
    Button,
    Checkbox,
    TagsInput
} from '@mantine/core';
import { IconMail, IconSend } from '@tabler/icons-react';
import { Business } from '@turizi/shared';
import { useSendAfipVoucherEmail } from '@/hooks/api-hooks';
import { formatPrice } from '@/lib/format-utils';
import { notify } from '@/lib/notify';

export interface SendAfipVoucherEmailTarget {
    id: number | string;
    pos?: number;
    voucher_number?: number | string;
    cbte_tipo?: number;
    receiver_name?: string;
    imp_total?: number;
    doc_nro?: string | number;
    reservations?: any[];
    business?: Business | null;
    client_business?: Business | null;
}

interface SendAfipVoucherEmailModalProps {
    opened: boolean;
    onClose: () => void;
    voucher: SendAfipVoucherEmailTarget | null;
    business?: Business | null;
    onSuccess?: () => void;
}

export default function SendAfipVoucherEmailModal({
    opened,
    onClose,
    voucher,
    business: initialBusiness,
    onSuccess
}: SendAfipVoucherEmailModalProps): JSX.Element | null {
    const { t } = useTranslation();
    const sendVoucherEmailMutation = useSendAfipVoucherEmail();

    const [selectedBusinessEmails, setSelectedBusinessEmails] = useState<string[]>([]);
    const [manualEmails, setManualEmails] = useState<string[]>([]);
    const [pendingManualEmail, setPendingManualEmail] = useState('');

    const getVoucherTypeName = (type: number) => {
        switch (type) {
            case 1: return 'Factura A';
            case 2: return 'Nota de Débito A';
            case 3: return 'Nota de Crédito A';
            case 6: return 'Factura B';
            case 7: return 'Nota de Débito B';
            case 8: return 'Nota de Crédito B';
            case 11: return 'Factura C';
            case 12: return 'Nota de Débito C';
            case 13: return 'Nota de Crédito C';
            case 195: return 'Factura T';
            case 196: return 'Nota de Débito T';
            case 197: return 'Nota de Crédito T';
            default: return `Comprobante (${type})`;
        }
    };

    // Extract business emails
    const businessEmails = useMemo(() => {
        if (!voucher) return [];
        const emailsSet = new Set<string>();

        const addEmailsFromBusiness = (b?: Business | null) => {
            if (!b) return;
            if (b.emails && typeof b.emails === 'object') {
                Object.values(b.emails).forEach((e) => {
                    if (typeof e === 'string' && e.trim()) {
                        emailsSet.add(e.trim().toLowerCase());
                    }
                });
            }
            if (b.email && typeof b.email === 'string' && b.email.trim()) {
                emailsSet.add(b.email.trim().toLowerCase());
            }
        };

        if (initialBusiness) {
            addEmailsFromBusiness(initialBusiness);
        }
        if (voucher.business) {
            addEmailsFromBusiness(voucher.business);
        }
        if (voucher.client_business) {
            addEmailsFromBusiness(voucher.client_business);
        }
        if (voucher.reservations && Array.isArray(voucher.reservations)) {
            voucher.reservations.forEach((res: any) => {
                addEmailsFromBusiness(res.client_business);
            });
        }

        return Array.from(emailsSet);
    }, [voucher, initialBusiness]);

    // Initialize selections unselected by default when voucher or modal opens
    useEffect(() => {
        if (opened && voucher) {
            setSelectedBusinessEmails([]);
            setManualEmails([]);
            setPendingManualEmail('');
        }
    }, [opened, voucher]);

    const toggleEmail = (email: string) => {
        setSelectedBusinessEmails((prev) =>
            prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
        );
    };

    const handleSelectAll = () => {
        setSelectedBusinessEmails([...businessEmails]);
    };

    const handleDeselectAll = () => {
        setSelectedBusinessEmails([]);
    };

    // Calculate all effective recipients
    const allRecipients = useMemo(() => {
        const set = new Set<string>();
        selectedBusinessEmails.forEach((e) => set.add(e.trim().toLowerCase()));
        manualEmails.forEach((e) => {
            if (e && typeof e === 'string' && e.trim()) {
                set.add(e.trim().toLowerCase());
            }
        });
        if (pendingManualEmail && pendingManualEmail.trim()) {
            set.add(pendingManualEmail.trim().toLowerCase());
        }
        return Array.from(set);
    }, [selectedBusinessEmails, manualEmails, pendingManualEmail]);

    const handleSend = async () => {
        if (!voucher || allRecipients.length === 0) return;
        try {
            await sendVoucherEmailMutation.mutateAsync({
                voucherId: voucher.id,
                sendTo: allRecipients
            });
            notify.success('success', 'voucher_email_sent_successfully');
            if (onSuccess) onSuccess();
            onClose();
        } catch (err: any) {
            notify.error('error', 'failed_to_send_voucher_email');
        }
    };

    if (!voucher) return null;

    const allSelected = businessEmails.length > 0 && selectedBusinessEmails.length === businessEmails.length;

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={
                <div className="font-bold text-sm text-gray-900">
                    {t('send_voucher_by_email')}
                </div>
            }
            centered
            size="md"
        >
            <div className="space-y-4 pt-2">
                {/* Voucher summary card */}
                <div className="text-xs text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-200 space-y-1">
                    <div>
                        <span className="font-medium text-gray-500">{t('voucher')}: </span>
                        <span className="font-semibold text-gray-800">
                            {voucher.cbte_tipo ? `${getVoucherTypeName(voucher.cbte_tipo)} ` : ''}
                            {voucher.pos ? `${String(voucher.pos).padStart(4, '0')}-` : ''}
                            {voucher.voucher_number !== undefined && voucher.voucher_number !== null
                                ? (typeof voucher.voucher_number === 'number'
                                    ? String(voucher.voucher_number).padStart(8, '0')
                                    : voucher.voucher_number)
                                : voucher.id}
                        </span>
                    </div>
                    {voucher.receiver_name && (
                        <div>
                            <span className="font-medium text-gray-500">{t('receiver')}: </span>
                            <span className="font-semibold text-gray-800">{voucher.receiver_name}</span>
                        </div>
                    )}
                    {voucher.reservations && voucher.reservations.length > 0 && (
                        <div>
                            <span className="font-medium text-gray-500">{t('reservations')}: </span>
                            <span className="font-semibold text-gray-800">
                                {voucher.reservations.map((r: any) => `#${r.id}`).join(', ')}
                            </span>
                        </div>
                    )}
                    {voucher.imp_total !== undefined && voucher.imp_total !== null && (
                        <div>
                            <span className="font-medium text-gray-500">{t('total')}: </span>
                            <span className="font-semibold text-gray-800">${formatPrice(voucher.imp_total)}</span>
                        </div>
                    )}
                </div>

                {/* Business emails selection */}
                {businessEmails.length > 0 ? (
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-700">
                                {t('business_emails')}
                            </span>
                            <Button
                                variant="subtle"
                                color="gray"
                                size="compact-xs"
                                onClick={allSelected ? handleDeselectAll : handleSelectAll}
                            >
                                {allSelected ? t('deselect_all') : t('select_all')}
                            </Button>
                        </div>

                        <div className="flex flex-col gap-1.5 max-h-48 overflow-y-auto pr-1">
                            {businessEmails.map((email) => {
                                const isChecked = selectedBusinessEmails.includes(email);
                                return (
                                    <div
                                        key={email}
                                        onClick={() => toggleEmail(email)}
                                        className={`flex items-center justify-between p-2.5 rounded-lg border text-xs cursor-pointer transition-colors ${isChecked
                                            ? 'bg-slate-50 border-slate-300 text-gray-900 font-medium'
                                            : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                                            }`}
                                    >
                                        <div className="flex items-center gap-2 min-w-0">
                                            <IconMail
                                                size={15}
                                                className={`shrink-0 ${isChecked ? 'text-slate-800' : 'text-gray-400'}`}
                                            />
                                            <span className="truncate select-none font-mono text-[11px]">{email}</span>
                                        </div>
                                        <Checkbox
                                            checked={isChecked}
                                            onChange={() => { }}
                                            size="xs"
                                            color="black"
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    <div className="text-xs text-amber-700 bg-amber-50 p-2.5 rounded-lg border border-amber-200">
                        {t('no_emails_configured')}
                    </div>
                )}

                {/* Additional manual emails input */}
                <div>
                    <TagsInput
                        label={businessEmails.length > 0 ? t('enter_additional_emails') : t('destination_email')}
                        placeholder="cliente@ejemplo.com"
                        value={manualEmails}
                        onChange={setManualEmails}
                        searchValue={pendingManualEmail}
                        onSearchChange={setPendingManualEmail}
                        onBlur={() => {
                            if (pendingManualEmail.trim()) {
                                const val = pendingManualEmail.trim().toLowerCase();
                                if (!manualEmails.includes(val)) {
                                    setManualEmails([...manualEmails, val]);
                                }
                                setPendingManualEmail('');
                            }
                        }}
                        clearable
                        leftSection={<IconMail size={16} />}
                        size="xs"
                    />
                </div>

                {/* Action buttons */}
                <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                    <Button
                        variant="subtle"
                        color="gray"
                        onClick={onClose}
                        disabled={sendVoucherEmailMutation.isPending}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        color="black"
                        onClick={handleSend}
                        loading={sendVoucherEmailMutation.isPending}
                        disabled={allRecipients.length === 0}
                        leftSection={<IconSend size={15} />}
                    >
                        {allRecipients.length > 1
                            ? `${t('send')} (${allRecipients.length})`
                            : t('send')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
